import { Component, OnInit, OnDestroy, computed, signal } from '@angular/core';
import { DefaultValueAccessor } from '@angular/forms';
import { ActivatedRouteSnapshot, NavigationEnd, NavigationError, Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { environment } from '../environments/environment';
import { isNoRolesError } from './auth/no-roles.interceptor';
import { HeaderComponent } from './core/components/header/header.component';
import { MainNavComponent } from './core/components/main-nav/main-nav.component';
import { SystemLockBannerComponent } from './core/components/system-lock-banner/system-lock-banner.component';
import { EnvironmentService } from './services/environment.service';
import { SidenavService } from './services/sidenav.service';
import { ThemeService } from './services/theme.service';
import { UserPermissionService } from './services/user-permission.service';
import { MatIconRegistry } from '@angular/material/icon';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [HeaderComponent, MainNavComponent, RouterOutlet, SystemLockBannerComponent]
})
export class AppComponent implements OnInit, OnDestroy {
  events: string[] = [];
  opened: boolean = true;
  title = 'Cmt.Web';

  /** False until the first navigation settles, so the shell never flashes before the roles guard runs. */
  private readonly navigated = signal(false);
  /** True when the active route (or an ancestor) has data.hideShell, e.g. the Not Authorized page. */
  private readonly hideShell = signal(false);
  /** Header + navigation are shown only for a settled navigation to a normal (shell) route. */
  readonly showChrome = computed(() => this.navigated() && !this.hideShell());

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private environment: EnvironmentService,
    protected sidenavService: SidenavService,
    private router: Router,
    private themeService: ThemeService,
    private matIconReg: MatIconRegistry,
    private userPermissionService: UserPermissionService,
    private authService?: MsalService,
    private msalBroadcastService?: MsalBroadcastService
  ) {
    this.environment.bootEnvironment();
    // Theme service will auto-initialize via constructor

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationError),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.hideShell.set(this.routeHidesShell(this.router.routerState.snapshot.root));
        this.navigated.set(true);
      });
  }

  /** Walks the active route chain; any route marked data.hideShell removes the shell. */
  private routeHidesShell(route: ActivatedRouteSnapshot | null): boolean {
    while (route) {
      if (route.data?.['hideShell']) {
        return true;
      }
      route = route.firstChild;
    }
    return false;
  }

  ngOnInit() {
    if (environment.useMockAuth) {
      console.log('🔓 Mock Authentication enabled - bypassing Entra login');
      this.loadPermissions();
      return;
    }
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined', 'mat-ligature-font');



    // Real MSAL initialization
    if (this.authService && this.msalBroadcastService) {
      // Handle redirect response from authentication
      this.authService.instance.handleRedirectPromise().then((response) => {
        if (response) {
          this.setActiveAccount();
          // Navigate to home page after successful login
          this.router.navigate(['/']);
        }
      }).catch((error) => {
        console.error('Error handling redirect:', error);
      });

      this.authService.instance.enableAccountStorageEvents();

      this.msalBroadcastService.msalSubject$
        .pipe(
          filter((msg: EventMessage) => msg.eventType === EventType.ACCOUNT_ADDED || msg.eventType === EventType.ACCOUNT_REMOVED),
          takeUntil(this._destroying$)
        )
        .subscribe((result: EventMessage) => {
          if (this.authService!.instance.getAllAccounts().length === 0) {
            this.userPermissionService.clear();
            window.location.pathname = "/";
          } else {
            this.setActiveAccount();
          }
        });

      this.msalBroadcastService.inProgress$
        .pipe(
          filter((status: InteractionStatus) => status === InteractionStatus.None),
          takeUntil(this._destroying$)
        )
        .subscribe(() => {
          this.setActiveAccount();

          // Permissions are loaded once here, after MSAL has settled and a token is obtainable.
          // load() is idempotent, so the repeated InteractionStatus.None emissions are harmless.
          if ((this.authService?.instance.getAllAccounts().length ?? 0) > 0) {
            this.loadPermissions();
          }
        });
    }
  }

  /**
   * Warms UserPermissionService so guards, the nav and *appHasPermission can answer synchronously.
   * Failures are logged rather than surfaced: the user simply sees no permissioned UI, and the API
   * still rejects anything they are not entitled to. A NO_ROLES response is expected for users with
   * no access; hasRolesGuard and NoRolesInterceptor route those users to Not Authorized, so it is
   * not logged as an error here.
   */
  private loadPermissions(): void {
    this.userPermissionService.load()
      .pipe(takeUntil(this._destroying$))
      .subscribe({
        error: (err: unknown) => {
          if (!isNoRolesError(err)) {
            console.error('Could not load user permissions', err);
          }
        },
      });
  }

  setActiveAccount() {
    if (!this.authService) return;

    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }
}

DefaultValueAccessor.prototype.registerOnChange = function (fn: (_: string | null) => void): void {
  this.onChange = (value: string | null) => {
    fn(value === '' ? null : value);
  };
};
